import type { SeriesPoint } from "./types";

export interface GdpCompositionPoint {
  date: string;
  c: number;
  g: number;
  i: number;
  xn: number;
  residual: number;
  status: "REAL" | "MODELO";
}

export interface InternationalComposition {
  country: string;
  period: string;
  c: number;
  g: number;
  i: number;
  xn: number;
  residual: number;
  source: string;
  sourceUrl: string;
}

export interface QuarterlyForecast {
  model: "SEASONAL_NAIVE" | "SEASONAL_MEDIAN" | "HOLT_DAMPED";
  label: string;
  rmse: number;
  mae: number;
  backtests: number;
  sampleSize: number;
  validationStatus: "VALIDADO" | "PROVISIONAL";
  forecast: Array<{
    date: string;
    value: number;
    lower80: number;
    upper80: number;
    lower95: number;
    upper95: number;
  }>;
}

export const INTERNATIONAL_GDP_COMPOSITION_2024: InternationalComposition[] = [
  {
    country: "Suecia",
    period: "2024",
    c: 45.2832281639828,
    g: 26.5704997625006,
    i: 25.1194850237718,
    xn: 54.4971396815443 - 51.4703526317996,
    residual: 0,
    source: "Banco Mundial · WDI (cuentas nacionales)",
    sourceUrl: "https://data.worldbank.org/indicator/NE.CON.PRVT.ZS?locations=SE",
  },
  {
    country: "Japón",
    period: "2024",
    c: 53.069552493906,
    g: 20.1443964769656,
    i: 27.6791711471936,
    xn: 21.9799093698461 - 22.8730294879112,
    residual: 0,
    source: "Banco Mundial · WDI (cuentas nacionales)",
    sourceUrl: "https://data.worldbank.org/indicator/NE.CON.PRVT.ZS?locations=JP",
  },
  {
    country: "Argentina",
    period: "2024",
    c: 66.6849067208421,
    g: 14.9167656550539,
    i: 15.8378002382311,
    xn: 15.2497485928824 - 12.6800122564846,
    residual: 100 - (66.6849067208421 + 14.9167656550539 + 15.8378002382311 + 15.2497485928824 - 12.6800122564846),
    source: "Banco Mundial · WDI (cuentas nacionales)",
    sourceUrl: "https://data.worldbank.org/indicator/NE.CON.PRVT.ZS?locations=AR",
  },
];

// INDEC, cuadro 11 de Oferta y Demanda Globales a precios corrientes.
// I = formación bruta de capital fijo + variación de existencias.
// El residual se recalcula como cierre de identidad con los decimales disponibles;
// desde 2024 se contrasta además con la discrepancia publicada por INDEC.
export const ARGENTINA_GDP_COMPOSITION_HISTORY: GdpCompositionPoint[] = [
  ["2019-Q1",71.532551,16.675043,11.080949,0.711457,0], ["2019-Q2",69.355036,16.318718,11.932517,2.393729,0],
  ["2019-Q3",64.782366,15.758320,16.596879,2.876335,0], ["2019-Q4",60.851273,16.993278,16.294413,5.956583,0],
  ["2020-Q1",70.440624,16.510036,10.158264,2.891076,0], ["2020-Q2",68.239887,18.245804,7.859375,5.654934,0],
  ["2020-Q3",63.345819,16.066558,16.968733,3.618890,0], ["2020-Q4",62.161955,16.820309,20.413478,0.604258,0],
  ["2021-Q1",66.953936,15.023040,15.543700,2.479324,0], ["2021-Q2",63.619715,14.733286,18.011593,3.635406,0],
  ["2021-Q3",60.921370,15.657907,18.826994,4.593729,0], ["2021-Q4",61.449659,17.755638,19.191246,1.603457,0],
  ["2022-Q1",68.563657,15.797641,14.993131,0.645571,0], ["2022-Q2",67.659717,14.891714,16.650648,0.797921,0],
  ["2022-Q3",64.312435,14.811449,21.504177,-0.628061,0], ["2022-Q4",64.460579,17.009351,15.855446,2.674625,0],
  ["2023-Q1",68.064251,16.388748,17.197976,-1.650975,0], ["2023-Q2",66.314336,16.864457,19.032658,-2.211452,0],
  ["2023-Q3",65.430541,15.632382,20.644450,-1.707373,0], ["2023-Q4",64.715473,16.198326,18.629342,0.456858,0],
  ["2024-Q1",69.088046,14.304888,10.793951,2.872180,2.940935], ["2024-Q2",64.794500,15.328055,13.175506,3.816670,2.885269],
  ["2024-Q3",67.706704,14.519723,19.774515,2.205754,-4.206697], ["2024-Q4",68.054605,16.172615,18.240526,2.213903,-4.681649],
  ["2025-Q1",77.040546,14.999260,13.606267,-1.468290,-4.177784], ["2025-Q2",69.448104,15.548907,15.572436,0.105589,-0.675035],
  ["2025-Q3",68.681038,14.643629,20.484313,1.020959,-4.829938], ["2025-Q4",69.274497,16.102384,16.623821,2.648264,-4.648967],
  ["2026-Q1",78.377239,14.747356,10.396187,1.339482,-4.860264],
].map(([date,c,g,i,xn]) => {
  const values = { c: Number(c), g: Number(g), i: Number(i), xn: Number(xn) };
  return { date: String(date), ...values, residual: 100 - values.c - values.g - values.i - values.xn, status: "REAL" as const };
});

const average = (values: number[]) => values.reduce((sum, value) => sum + value, 0) / Math.max(1, values.length);
const median = (values: number[]) => {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
};
const quantile = (sorted: number[], probability: number) => {
  const position = Math.max(0, Math.min(sorted.length - 1, (sorted.length - 1) * probability));
  const lower = Math.floor(position);
  const upper = Math.ceil(position);
  const weight = position - lower;
  return sorted[lower] * (1 - weight) + sorted[upper] * weight;
};
const nextQuarter = (date: string, steps: number) => {
  const match = date.match(/^(\d{4})-Q([1-4])$/);
  if (!match) throw new Error(`Trimestre inválido: ${date}`);
  const zeroBased = Number(match[1]) * 4 + Number(match[2]) - 1 + steps;
  return `${Math.floor(zeroBased / 4)}-Q${(zeroBased % 4) + 1}`;
};
function seededRandom(seed: number) {
  let state = seed >>> 0;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 4294967296;
  };
}
function dampedFit(values: number[], alpha: number, beta: number, phi: number) {
  let level = values[0];
  let trend = values.length > 1 ? values[1] - values[0] : 0;
  for (let index = 1; index < values.length; index++) {
    const previous = level;
    level = alpha * values[index] + (1 - alpha) * (level + phi * trend);
    trend = beta * (level - previous) + (1 - beta) * phi * trend;
  }
  return { level, trend, phi };
}
function optimizeDamped(values: number[]) {
  let best = { ...dampedFit(values, 0.4, 0.2, 0.9), sse: Number.POSITIVE_INFINITY };
  for (const alpha of [0.2, 0.4, 0.6, 0.8]) {
    for (const beta of [0.1, 0.2, 0.4]) {
      for (const phi of [0.8, 0.9, 0.98]) {
        let sse = 0;
        for (let index = 8; index < values.length; index++) {
          const fit = dampedFit(values.slice(0, index), alpha, beta, phi);
          sse += (fit.level + phi * fit.trend - values[index]) ** 2;
        }
        if (sse < best.sse) best = { ...dampedFit(values, alpha, beta, phi), sse };
      }
    }
  }
  return best;
}

type ModelId = QuarterlyForecast["model"];

function predict(model: ModelId, train: number[], horizon = 1) {
  if (model === "SEASONAL_NAIVE") {
    const index = train.length - 4 + ((horizon - 1) % 4);
    return train[Math.max(0, Math.min(train.length - 1, index))];
  }
  if (model === "SEASONAL_MEDIAN") {
    const targetQuarter = (train.length + horizon - 1) % 4;
    const comparable = train.filter((_, index) => index % 4 === targetQuarter).slice(-4);
    return median(comparable.length ? comparable : train.slice(-4));
  }
  const fit = optimizeDamped(train);
  const dampedSum = Array.from({ length: horizon }, (_, index) => fit.phi ** (index + 1)).reduce((sum, value) => sum + value, 0);
  return fit.level + dampedSum * fit.trend;
}

export function buildQuarterlyForecast(series: SeriesPoint[], horizon = 3): QuarterlyForecast {
  const clean = [...series].filter((point) => Number.isFinite(point.value)).sort((a, b) => a.date.localeCompare(b.date));
  if (clean.length < 20) throw new Error("Se requieren al menos 20 trimestres observados");
  const values = clean.map((point) => point.value);
  const models: ModelId[] = ["SEASONAL_NAIVE", "SEASONAL_MEDIAN", "HOLT_DAMPED"];
  const labels: Record<ModelId, string> = {
    SEASONAL_NAIVE: "Naive estacional (mismo trimestre previo)",
    SEASONAL_MEDIAN: "Mediana robusta del mismo trimestre",
    HOLT_DAMPED: "Holt amortiguado",
  };
  const start = 12;
  const actual = values.slice(start);
  const scored = models.map((model) => {
    const predicted = actual.map((_, offset) => predict(model, values.slice(0, start + offset), 1));
    const errors = actual.map((value, index) => predicted[index] - value);
    return {
      model,
      label: labels[model],
      mae: average(errors.map(Math.abs)),
      rmse: Math.sqrt(average(errors.map((error) => error ** 2))),
      residuals: actual.map((value, index) => value - predicted[index]),
    };
  }).sort((a, b) => a.rmse - b.rmse);
  const winner = scored[0];
  const centered = winner.residuals.map((value) => value - average(winner.residuals));
  const random = seededRandom(2030);
  const simulated = Array.from({ length: horizon }, () => [] as number[]);
  for (let simulation = 0; simulation < 10_000; simulation++) {
    for (let step = 0; step < horizon; step++) {
      simulated[step].push(centered[Math.floor(random() * centered.length)]);
    }
  }
  simulated.forEach((valuesAtStep) => valuesAtStep.sort((a, b) => a - b));
  return {
    model: winner.model,
    label: winner.label,
    rmse: winner.rmse,
    mae: winner.mae,
    backtests: actual.length,
    sampleSize: values.length,
    validationStatus: actual.length >= 12 ? "VALIDADO" : "PROVISIONAL",
    forecast: Array.from({ length: horizon }, (_, index) => {
      const value = predict(winner.model, values, index + 1);
      const errors = simulated[index];
      return {
        date: nextQuarter(clean.at(-1)!.date, index + 1),
        value,
        lower80: value + quantile(errors, 0.1),
        upper80: value + quantile(errors, 0.9),
        lower95: value + quantile(errors, 0.025),
        upper95: value + quantile(errors, 0.975),
      };
    }),
  };
}

export function reconcileComposition(c: number, g: number, i: number, xn: number, date: string, status: "REAL" | "MODELO"): GdpCompositionPoint {
  return { date, c, g, i, xn, residual: 100 - c - g - i - xn, status };
}
